import type {
  ArchiveEvent,
  AssetCondition,
  Category,
  ChannelResult,
  CollectionConditionDetail,
  CollectionRun,
  GuidelineFolder,
  GuidelineStep,
  GuidelineTool,
  Me,
  NavigationResponse,
  Permission,
  RankingFilters,
  RankingResponse,
  RecommendationRun,
  UserRecord,
  VideoResult,
} from './types';
import { request, toQuery } from './apiClient';

export const api = {
  me: () => request<Me>('/api/me'),
  navigation: () => request<NavigationResponse>('/api/navigation'),
  categories: () => request<{ categories: Category[] }>('/api/categories'),
  users: () => request<{ users: UserRecord[] }>('/api/users'),
  createUser: (body: Record<string, unknown>) => request<{ user: UserRecord }>('/api/users', {
    method: 'POST',
    body: JSON.stringify(body),
  }),
  updateUser: (id: number, body: Record<string, unknown>) => request<{ user: UserRecord }>(`/api/users/${id}`, {
    method: 'PATCH',
    body: JSON.stringify(body),
  }),
  addUserPermission: (id: number, body: Record<string, unknown>) => request<{ permissions: Permission[] }>(`/api/users/${id}/permissions`, {
    method: 'POST',
    body: JSON.stringify(body),
  }),
  deleteUserPermission: (userId: number, permissionId: number) => request<{ permissions: Permission[] }>(`/api/users/${userId}/permissions/${permissionId}`, {
    method: 'DELETE',
  }),
  keys: () => request<Array<{ id: number; label: string; status: string; maskedKey: string; storage: string }>>('/api/keys'),
  createKey: (body: { key: string; label?: string }) => request<{ id: number; label: string; status: string; maskedKey: string; storage: string }>('/api/keys', {
    method: 'POST',
    body: JSON.stringify(body),
  }),
  settings: () => request<{ settings: Record<string, unknown> }>('/api/settings'),
  updateSetting: (key: string, value: unknown) => request<{ key: string; value: unknown }>(`/api/settings/${encodeURIComponent(key)}`, {
    method: 'PATCH',
    body: JSON.stringify({ value }),
  }),
  assets: (params: { target?: string; type?: string; editorEmail?: string } = {}) => request<{ conditions: AssetCondition[] }>(`/api/assets/conditions${toQuery(params)}`),
  createAsset: (body: Record<string, unknown>) => request<{ condition: AssetCondition }>('/api/assets/conditions', {
    method: 'POST',
    body: JSON.stringify(body),
  }),
  updateAsset: (id: number, body: Record<string, unknown>) => request<{ condition: AssetCondition }>(`/api/assets/conditions/${id}`, {
    method: 'PATCH',
    body: JSON.stringify(body),
  }),
  deleteAsset: (id: number) => request<{ id: number; isActive: false }>(`/api/assets/conditions/${id}`, { method: 'DELETE' }),
  collectionRuns: (editorEmail: string) => request<{ runs: CollectionRun[]; total: number }>(`/api/collection/runs${toQuery({ editorEmail })}`),
  collectionVideos: (params: Record<string, string | number> = {}) => request<{ videos: VideoResult[]; total: number }>(`/api/collection/videos${toQuery(params)}`),
  createCollectionRun: (body: Record<string, unknown>) => request<{ run: CollectionRun; collected: number; details: CollectionConditionDetail[] }>('/api/collection/runs', {
    method: 'POST',
    body: JSON.stringify(body),
  }),
  retryCollectionRun: (id: number) => request<{ run: CollectionRun; collected: number; details: CollectionConditionDetail[]; reused: boolean }>(`/api/collection/runs/${id}/retry`, {
    method: 'POST',
  }),
  recommendationRuns: (params: { formType?: string; editorEmail?: string } = {}) => request<{ runs: RecommendationRun[]; total: number }>(`/api/recommendations${toQuery(params)}`),
  recommendationRun: (id: number) => request<{ run: RecommendationRun; items: VideoResult[] }>(`/api/recommendations/${id}`),
  generateRecommendation: (body: Record<string, unknown>) => request<{ run: RecommendationRun; items: VideoResult[]; skipped?: boolean; reason?: string }>('/api/recommendations/generate', {
    method: 'POST',
    body: JSON.stringify(body),
  }),
  regenerateRecommendation: (body: Record<string, unknown>) => request<{ run: RecommendationRun; items: VideoResult[]; skipped?: boolean; reason?: string }>('/api/recommendations/regenerate', {
    method: 'POST',
    body: JSON.stringify(body),
  }),
  addRecommendationItem: (runId: number, body: Record<string, unknown>) => request<{ run: RecommendationRun; item: VideoResult }>(`/api/recommendations/${runId}/items`, {
    method: 'POST',
    body: JSON.stringify(body),
  }),
  updateRecommendationItem: (id: number, body: Record<string, unknown>) => request<{ item: VideoResult }>(`/api/recommendations/items/${id}`, {
    method: 'PATCH',
    body: JSON.stringify(body),
  }),
  recommendationToExample: (id: number) => request<{ item: VideoResult; duplicated: boolean }>(`/api/recommendations/items/${id}/example`, { method: 'POST' }),
  recommendationToArchive: (id: number) => request<{ item: VideoResult; duplicated: boolean }>(`/api/recommendations/items/${id}/archive`, { method: 'POST' }),
  examples: (sort = 'latest', formType = '', editorEmail = '') => request<{ items: VideoResult[]; total: number }>(`/api/examples${toQuery({ sort, formType, editorEmail })}`),
  addExample: (body: Record<string, unknown>) => request<{ item: VideoResult; duplicated: boolean }>('/api/examples', {
    method: 'POST',
    body: JSON.stringify(body),
  }),
  updateExample: (id: number, body: Record<string, unknown>) => request<{ item: VideoResult }>(`/api/examples/${id}`, {
    method: 'PATCH',
    body: JSON.stringify(body),
  }),
  deleteExample: (id: number) => request<null>(`/api/examples/${id}`, { method: 'DELETE' }),
  archive: (params: Record<string, string | number> = {}) => request<{ items: VideoResult[]; total: number }>(`/api/archive${toQuery(params)}`),
  addArchive: (body: Record<string, unknown>) => request<{ item: VideoResult; duplicated: boolean }>('/api/archive', {
    method: 'POST',
    body: JSON.stringify(body),
  }),
  updateArchive: (id: number, body: Record<string, unknown>) => request<{ item: VideoResult }>(`/api/archive/${id}`, {
    method: 'PATCH',
    body: JSON.stringify(body),
  }),
  archiveEvents: (id: number) => request<{ events: ArchiveEvent[] }>(`/api/archive/${id}/events`),
  deleteArchive: (id: number) => request<null>(`/api/archive/${id}`, { method: 'DELETE' }),
  guidelines: (editorEmail = '') => request<{ steps: GuidelineStep[]; toolFolders: GuidelineFolder[] }>(`/api/guidelines${toQuery({ editorEmail })}`),
  updateGuidelineProgress: (stepId: number, body: { isCompleted: boolean; editorEmail?: string }) => request<{ progress: Record<string, unknown> }>(`/api/guidelines/progress/${stepId}`, {
    method: 'PATCH',
    body: JSON.stringify(body),
  }),
  createGuidelineStep: (body: Record<string, unknown>) => request<{ step: GuidelineStep }>('/api/guidelines/steps', {
    method: 'POST',
    body: JSON.stringify(body),
  }),
  updateGuidelineStep: (id: number, body: Record<string, unknown>) => request<{ item: GuidelineStep }>(`/api/guidelines/steps/${id}`, {
    method: 'PATCH',
    body: JSON.stringify(body),
  }),
  createToolFolder: (body: Record<string, unknown>) => request<{ folder: GuidelineFolder }>('/api/guidelines/tool-folders', {
    method: 'POST',
    body: JSON.stringify(body),
  }),
  updateToolFolder: (id: number, body: Record<string, unknown>) => request<{ item: GuidelineFolder }>(`/api/guidelines/tool-folders/${id}`, {
    method: 'PATCH',
    body: JSON.stringify(body),
  }),
  createGuidelineTool: (body: Record<string, unknown>) => request<{ tool: GuidelineTool }>('/api/guidelines/tools', {
    method: 'POST',
    body: JSON.stringify(body),
  }),
  updateGuidelineTool: (id: number, body: Record<string, unknown>) => request<{ item: GuidelineTool }>(`/api/guidelines/tools/${id}`, {
    method: 'PATCH',
    body: JSON.stringify(body),
  }),
  rankings: (filters: RankingFilters) => request<RankingResponse<VideoResult | ChannelResult>>(`/api/rankings/youtube${toQuery(filters)}`),
  searchChannels: (params: { q: string; country?: string; limit?: number }) =>
    request<RankingResponse<ChannelResult>>(`/api/search/channels${toQuery(params)}`),
  similarChannels: (params: { channelId?: string; q?: string; country?: string; limit?: number }) =>
    request<RankingResponse<ChannelResult> & { sourceChannel?: ChannelResult | null }>(`/api/similar/channels${toQuery(params)}`),
  searchVideos: (params: {
    q: string;
    country?: string;
    videoCategoryId?: string;
    order?: string;
    period?: string;
    limit?: number;
  }) => request<RankingResponse<VideoResult>>(`/api/search/videos${toQuery(params)}`),
  similarVideos: (params: { videoId: string; q?: string; country?: string; limit?: number }) =>
    request<RankingResponse<VideoResult> & { sourceVideo?: VideoResult | null }>(`/api/similar/videos${toQuery(params)}`),
};
